// web/src/components/PlanCapReached.tsx
//
// "What a season so far" — full-screen celebration + upgrade screen
// that fires when a Free user trips a plan cap (added 2026-05-26 as F1
// of the tier-gap analysis).
//
// Today's path was: 403 plan_limit_reached → inline red error text.
// That's the highest-intent moment in the funnel (the parent has put 3
// months of muscle memory into the app) and we were squandering it.
//
// Design intent: NOT a paywall. A celebration. The parent crossed a
// real milestone — 10 matches recorded — and the right framing is
// "what a season, here's how to keep going" not "you've hit the cap,
// pay up". Magazine treatment carries through; eyebrow + headline +
// stats strip + value pitch + two CTAs.
//
// Use:
//   {capHit ? (
//     <PlanCapReached cap="matches" onDismiss={() => setCapHit(false)} />
//   ) : null}
//
// The component is presentation-only — the parent decides when to
// render it. Cap detection sits in the form's catch block (look for
// e.status === 403 + e.code === 'plan_limit_reached').

import { useEffect, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

type CapKind = 'matches' | 'kids' | 'photos';

interface CopyForCap {
  eyebrow: string;
  headline: string;
  subhead: string;
  cta: string;
}

/**
 * Build a tiny copy bundle for the cap that was hit. The copy is
 * deliberately celebratory + concrete — names a real number rather
 * than "you've hit the cap".
 */
function copyFor(cap: CapKind, currentCount?: number): CopyForCap {
  switch (cap) {
    case 'matches':
      return {
        eyebrow: `${currentCount ?? 10} of 10 matches recorded`,
        headline: 'What a season so far',
        subhead:
          "You've captured every match of the season Free included. " +
          'To keep the story going — and unlock the household, the ' +
          'weekly digest, and 4 more kids — switch to Stats.',
        cta: 'Continue the season with Stats',
      };
    case 'kids':
      return {
        eyebrow: '1 of 1 kid on your account',
        headline: 'Add the whole squad',
        subhead:
          "Free includes one kid. Stats lifts that to five — siblings, " +
          'cousins, the whole 5-a-side team — and lets a co-parent or ' +
          "grandparent into the household to see every match.",
        cta: 'Add more kids with Stats',
      };
    case 'photos':
      return {
        eyebrow: '1 of 1 photo for this match',
        headline: 'More than one moment per match',
        subhead:
          'Five photos per match on Stats — every goal, the team huddle, ' +
          "the medal photo. Plus the household share so grandma sees them all.",
        cta: 'Upgrade to Stats for more photos',
      };
  }
}

export interface PlanCapReachedProps {
  cap: CapKind;
  /** Optional — current count for the eyebrow. Defaults to the cap. */
  currentCount?: number;
  /** Optional — close handler. If omitted, the screen has no dismiss. */
  onDismiss?: () => void;
}

export function PlanCapReached({
  cap,
  currentCount,
  onDismiss,
}: PlanCapReachedProps): ReactElement {
  const copy = copyFor(cap, currentCount);

  // Lock body scroll while this is showing — it's full-viewport.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfj-plan-cap-headline"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background:
          'linear-gradient(180deg, var(--green) 0%, var(--green-dark, #14271a) 100%)',
        color: 'var(--ink-on-green)',
        display: 'flex',
        flexDirection: 'column',
        padding: 'var(--space-lg)',
        overflowY: 'auto',
        animation: 'mfj-plan-cap-in 320ms ease-out',
      }}
    >
      {/* Masthead — tiny + chic, top-left. */}
      <div
        style={{
          fontFamily: 'var(--font-masthead)',
          fontStyle: 'italic',
          fontSize: '1.25rem',
          textTransform: 'uppercase',
          letterSpacing: '-0.01em',
          color: 'var(--ink-on-green)',
          opacity: 0.85,
        }}
      >
        MyFootballJournal
      </div>

      {/* Spacer to push the main block toward the middle of the viewport */}
      <div style={{ flex: '1 0 8vh' }} />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-base)',
          maxWidth: 540,
          margin: '0 auto',
          width: '100%',
        }}
      >
        {/* Eyebrow — the specific number, sets the celebratory tone. */}
        <div
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--micro)',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--gold-light)',
            fontWeight: 700,
          }}
        >
          {copy.eyebrow}
        </div>

        {/* Headline — magazine treatment. Big, italic, low line-height. */}
        <h1
          id="mfj-plan-cap-headline"
          style={{
            fontFamily: 'var(--font-masthead)',
            fontStyle: 'italic',
            textTransform: 'uppercase',
            fontSize: 'clamp(2.4rem, 9vw, 4rem)',
            lineHeight: 0.95,
            letterSpacing: '-0.015em',
            margin: 0,
            color: 'var(--ink-on-green)',
          }}
        >
          {copy.headline}
        </h1>

        {/* Subhead — the why, in body voice. */}
        <p
          style={{
            margin: '8px 0 0',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--body)',
            lineHeight: 1.55,
            color: 'rgba(250,245,236,0.92)',
            maxWidth: 480,
          }}
        >
          {copy.subhead}
        </p>

        {/* Mini value table — Stats highlights in one breath */}
        <div
          style={{
            marginTop: 'var(--space-base)',
            padding: 'var(--space-base)',
            background: 'rgba(0,0,0,0.22)',
            borderLeft: '3px solid var(--gold)',
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            fontSize: 'var(--small)',
            color: 'rgba(250,245,236,0.95)',
          }}
        >
          <div>
            <strong style={{ color: 'var(--gold-light)' }}>5 kids</strong> · siblings, cousins, the whole 5-a-side
          </div>
          <div>
            <strong style={{ color: 'var(--gold-light)' }}>200 matches</strong> per season per kid
          </div>
          <div>
            <strong style={{ color: 'var(--gold-light)' }}>5 photos</strong> per match
          </div>
          <div>
            <strong style={{ color: 'var(--gold-light)' }}>Family share</strong> · co-parent + grandparents see every match
          </div>
          <div>
            <strong style={{ color: 'var(--gold-light)' }}>Weekly digest</strong> · Monday morning round-up
          </div>
          <div>
            <strong style={{ color: 'var(--gold-light)' }}>Saturday matchday prompt</strong> · never forget to log
          </div>
        </div>

        {/* CTAs */}
        <div
          style={{
            marginTop: 'var(--space-lg)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <Link
            to="/upgrade"
            style={{
              display: 'block',
              padding: '16px 20px',
              background: 'var(--electric-yellow, #f6d24a)',
              color: '#1a1306',
              textAlign: 'center',
              borderRadius: 999,
              fontFamily: 'var(--font-masthead)',
              fontStyle: 'italic',
              fontSize: '1.05rem',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 700,
              textDecoration: 'none',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            }}
          >
            {copy.cta} · £1.99/mo
          </Link>
          {onDismiss ? (
            <button
              type="button"
              onClick={onDismiss}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(250,245,236,0.7)',
                fontSize: 'var(--small)',
                padding: 12,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              Maybe later
            </button>
          ) : null}
        </div>
      </div>

      <div style={{ flex: '1 0 6vh' }} />

      <style>{`
        @keyframes mfj-plan-cap-in {
          from { opacity: 0; transform: scale(0.98); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
